<template>
	<div class="view-wrapper slug-page" v-if="document">
		<div class="container">
			<h1>{{ document.title }}</h1>
			<div>
				<c-flex-content :content="document.flex_content" />
			</div>
		</div>
	</div>
</template>

<script>
import * as utils from '@/utils/utils'
import defaultHeadData from '@/utils/defaultHeadData'
import getPageData from '@/api/slugpage.wp.api'
import CFlexContent from '@/components/FlexContent'

export default {
	name: 'slugpage',
	components: { CFlexContent },
	data: () => ({
		documentData: {},
		team: {},
		headData: defaultHeadData
	}),
	computed: {
		settings() {
			return this.$store.state.general.settings
		},
		defaultMeta() {
			return this.$store.state.general.siteSettings[this.settings.currentLang].defaultMeta
		},
		siteSettings() {
			return this.$store.state.general.siteSettings
		},
		activeRouteUrl() {
			return utils.constructActiveRouteUrl(this.$route.fullPath)
		},
		document() {
			return this.documentData
		}
	},
	watch: {
		'$route': function() {
			this.setPageData()
			this.updateHeadData()
		},
		defaultMeta: function() {
			this.updateHeadData()
		}
	},
	head: {
		title: function() {
			return utils.transformHeadTitle(this.headData.title)
		},
		meta: function() {
			return utils.transformHeadMeta(this.headData.meta)
		},
		link: function() {
			return utils.transformHeadLink({ data: this.headData.link, i18n: true })
		}
	},
	beforeMount() {
		this.setPageData()
		this.$store.dispatch('setAltLanguageLink', { lang: 'de-ch', link: '/' })
		this.$store.dispatch('setAltLanguageLink', { lang: 'en-us', link: '/en/' })

	},
	methods: {
		cleanSlug(path) {
			let cleanPath = ''
			if (path.startsWith('/en/')) {
				cleanPath = path.substr(3)
			} else {
				cleanPath = path
			}
			if (cleanPath.startsWith('/')) {
				cleanPath = cleanPath.substr(1)
			}
			if (cleanPath.endsWith('/')) {
				cleanPath = cleanPath.slice(0, -1)
			}
			return cleanPath
		},
		setPageData() {
			this.documentData = null
			getPageData({ lang: this.settings.currentLang, slug: this.cleanSlug(this.$route.path) })
				.then(payload => {
					this.documentData = payload
					utils.setHtmlLang(this.settings.currentLang)
					this.updateHeadData()
					this.updateAltLanguageLinks()
				})
				.catch(error => {
					document.location.href = '../404'
				})
		},
		updateAltLanguageLinks() {
			const current = this.documentData.polylang.current.lang
			let enTranslationSlug = ''
			let deTranslationSlug = ''

			if (this.documentData.polylang.translations && this.documentData.polylang.translations.en && this.documentData.polylang.translations.en.slug) {
				enTranslationSlug = this.documentData.polylang.translations.en.slug
			}
			if (this.documentData.polylang.translations && this.documentData.polylang.translations.de && this.documentData.polylang.translations.de.slug) {
				deTranslationSlug = this.documentData.polylang.translations.de.slug
			}

			if (current === 'de') {
				this.$store.dispatch('setAltLanguageLink', { lang: 'de-ch', link: this.$route.fullPath })
				this.$store.dispatch('setAltLanguageLink', { lang: 'en-us', link: `/en/${enTranslationSlug}` })
			} else if (current === 'en') {
				this.$store.dispatch('setAltLanguageLink', { lang: 'de-ch', link: `/${deTranslationSlug}` })
				this.$store.dispatch('setAltLanguageLink', { lang: 'en-us', link: this.$route.fullPath })
			}
		},
		updateHeadData() {
			if (this.siteSettings[this.settings.currentLang].defaultMeta && this.documentData) {
				this.headData = utils.transformHeadData({
					meta: this.documentData.meta,
					defaultMeta: this.siteSettings[this.settings.currentLang].defaultMeta,
					settings: this.settings,
					activeRouteUrl: this.activeRouteUrl
				})
				this.$emit('updateHead')
			}
		}
	}
}
</script>

<style lang="scss">

	@import "../assets/css/helpers";

	$maxContentWidth: 45rem;

	.slug-page {
		padding-top: 3rem;
		padding-bottom: 4rem;

		.c-flex-content__image-wrapper--full {
			max-width: 45rem;
		}

		.c-flex-content__slice--html_snippet {
			position: relative;
			width: 100%;
			max-width: 45rem;
		}

		&__meta {
			margin-top: -4rem;
			margin-bottom: 3rem;
			margin-left: -1rem;
		}
		&__date {
			display: inline-block;
			margin: 0;
			font-size: 1rem;
			color: #888;
			background-color: #fff;
			padding: 0 1rem;
			font-weight: 600;
		}
		p {
			max-width: $maxContentWidth;
			margin: .75rem 0;
			line-height: 1.6;
		}
		h1 {
			display: inline-block;
			margin-bottom: 0;
			line-height: 1.2;
			max-width: 45rem;
			background-color: #fff;
		}
		h2, h3 {
			font-weight: 600;
			margin-top: 3rem;
			margin-bottom: .5rem;
			line-height: 1.2;
			max-width: $maxContentWidth;

			@include mq($from: medium) {
				margin-bottom: 1.5rem;
			}
		}
		h3 {
			font-size: 1.25rem;
		}
		.c-embed {
			max-width: $maxContentWidth;
			margin: 2rem 0;

			@include mq($from: medium) {
				margin: 3rem 0;
			}
		}

		&__related {
			margin-top: 4rem;

			h2 {
				margin-bottom: 2rem;
				color: $colorBrandMain;
			}
			h3 {
				margin-top: 0;
				margin-bottom: 0;
				line-height: 1.4;
			}
			p {
				margin: 0;
			}

			@include mq($from: medium) {
				margin-top: 6rem;
			}
		}

		&_footer-bildquelle {
			color: #83929C;
		}

		&__content {
			ul {
				list-style: disc inside none;
				text-indent: -2em;
				padding-left: 2em;
			}

			ol {
				display: table;
				margin-left: 0;
				padding-left: 0;
				list-style: none;

				li {
					display: table-row;
					counter-increment: table-ol;

					&:before {
						content: counter(table-ol) ".";
						display: table-cell;
						padding-right: 0.8em;
						text-align: right;
					}
				}
			}
		}
	}

</style>



// WEBPACK FOOTER //
// SlugPage.vue?5cf3eff8